import {
  AnimationToggle,
  OnboardingLayout,
  CareIntensityAnimation,
  useSlideSetup,
  useAnimationPreferences,
} from '@/features/client-onboarding';

import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { selectClient, selectIntakeSurvey } from '@/model';
import { possessive, formatDurationYears, isDurationCustom } from '@shared';
import {
  OnboardingPredictedCareNeeds,
  ColumnLengthOfCareYears,
} from '@/pages/OnboardingPredictedCareNeeds';

export function GenworthOnboardingPredictedCareNeeds(_props: ClientIdProps) {
  const client = useSelector(selectClient);
  const {
    clientFirstName,
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = client;
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { animationsEnabled } = useAnimationPreferences();
  const { flags } = useFeatureFlags();
  const showAnimations = flags.animatedSlides && animationsEnabled;

  const lengthOfCareYears = allPhaseCareMonthsNeeded / 12;

  useSlideSetup({
    slidePage: OnboardingPredictedCareNeeds,
    nextButtonLabel: `See ${possessive(clientFirstName)} Costs`,
  });

  const narratives: Record<string, string> = {
    early_care: `For people like ${clientFirstName}, claims usually begin with lighter assistance, such as help with household tasks or transportation.`,
    duration: `Here's the typical length of claim for people like ${clientFirstName} who go on to claim, about ${formatDurationYears(lengthOfCareYears)} years.`,
    late_care: `Over time, care needs typically progress and require more intensive support for daily activities like bathing, dressing, and mobility.`,
  };

  return (
    <OnboardingLayout>
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="w-full py-2 md:py-8">
          <div className="mb-6 flex w-full grow items-center justify-between gap-6">
            <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
              What a Typical Claim Looks Like for People Like {clientFirstName}
            </h1>
            {flags.animatedSlides && (
              <div className="flex items-center gap-2">
                <AnimationToggle />
              </div>
            )}
          </div>
          {showAnimations ? (
            <CareIntensityAnimation
              clientFirstName={clientFirstName}
              lengthOfCareYears={lengthOfCareYears}
              initialHoursPerWeek={0}
              laterHoursPerWeek={0}
              intakeSurvey={intakeSurvey}
              isDurationEdited={isDurationCustom(phaseCosts)}
              hideHoursPerWeek
              lengthLabel="Typical Length of Claim"
              narratives={narratives}
            />
          ) : (
            <div className="flex w-full grow justify-center">
              <ColumnLengthOfCareYears />
            </div>
          )}
        </div>
        <div className="mt-auto" />
      </div>
    </OnboardingLayout>
  );
}
